import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { PrismSource } from "./prism-retrieval-constants"

import { cn } from "@/lib/utils"

type PrismSourceChipProps = {
  source: PrismSource
  /**
   * The answer's own citation click handler, called with the same 0-based
   * citation index a click on the answer's superscript passes. Without it the
   * chip is plain text.
   */
  onOpen?: (citationIndex: number) => void
  className?: string
  children: ReactNode
}

/**
 * A named Prism source (#3261). With `onOpen` it is a button that does what
 * clicking the same citation in the answer does; the citation system itself is
 * unchanged. The number is the answer's own superscript number.
 */
export function PrismSourceChip({ source, onOpen, className, children }: PrismSourceChipProps) {
  const { t } = useTranslation()
  const title = source.spaceLabel ? `${source.title} · ${source.spaceLabel}` : source.title

  if (!onOpen) {
    return (
      <span className={className} data-prism-source={source.n} title={title}>
        {children}
      </span>
    )
  }

  return (
    <button
      data-prism-source={source.n}
      title={title}
      type="button"
      aria-label={t("activity_messages.filth.openSource", {
        n: source.n,
        title: source.title,
        defaultValue: "Show source {{n}}: {{title}}",
      })}
      className={cn(
        "focus-visible:outline-foreground cursor-pointer text-left focus-visible:outline-2 focus-visible:-outline-offset-2",
        className
      )}
      onClick={() => onOpen(source.n - 1)}
    >
      {children}
    </button>
  )
}
